/**
 * Contact Fragment - ES6 Module Version
 * Handles contact list selection, bulk operations, and contact selector modal
 */
import { Events } from '../../../js/utils/events.js';

let initialized = false;

/**
 * Build a recipient string for the transfer form. Names containing the server's
 * recipient delimiters (`,`, `;`, CR, LF) are wrapped in double quotes so the
 * server parser keeps the full name as a single entry; stray double quotes in
 * the name are stripped before wrapping.
 *
 * @param {string} name
 * @param {string} email
 * @returns {string}
 */
function formatRecipient(name, email) {
    if (!name || name === email) {
        return email;
    }
    if (/[",;\r\n]/.test(name)) {
        const escaped = name.replace(/"/g, '');
        return `"${escaped}" <${email}>`;
    }
    return `${name} <${email}>`;
}

// ============================================================================
// Contact List Handlers
// ============================================================================

const ContactHandlers = {
    /**
     * Handle "select all" checkbox change
     */
    onSelectAll(e, element) {
        const container = element.closest('#contact-list');
        if (!container) return;

        const rowCheckboxes = container.querySelectorAll('[data-contact-row-checkbox]');
        rowCheckboxes.forEach(cb => {
            cb.checked = element.checked;
        });

        // Programmatic .checked does not fire `change`, so HTMX's
        // hx-trigger="change from:[data-contact-row-checkbox]" wouldn't
        // see the bulk flip. Dispatch one bubbling change event; the
        // form's delay:50ms collapses repeats so one is enough.
        if (rowCheckboxes.length > 0) {
            rowCheckboxes[0].dispatchEvent(new Event('change', { bubbles: true }));
        }
    },

    /**
     * Handle individual row checkbox change
     */
    onRowCheckboxChange(e, element) {
        const container = element.closest('#contact-list');
        if (!container) return;

        ContactHandlers.updateSelectAllState(container);
    },

    /**
     * Update "select all" checkbox state based on row selections
     */
    updateSelectAllState(container) {
        const selectAllCheckbox = container.querySelector('[data-contact-select-all]');
        if (!selectAllCheckbox) return;

        const rowCheckboxes = container.querySelectorAll('[data-contact-row-checkbox]');
        const checkedCount = container.querySelectorAll('[data-contact-row-checkbox]:checked').length;

        if (checkedCount === 0) {
            selectAllCheckbox.checked = false;
            selectAllCheckbox.indeterminate = false;
        } else if (checkedCount === rowCheckboxes.length) {
            selectAllCheckbox.checked = true;
            selectAllCheckbox.indeterminate = false;
        } else {
            selectAllCheckbox.checked = false;
            selectAllCheckbox.indeterminate = true;
        }
    },

};

// ============================================================================
// Contact Modal Handlers (selector_modal.jinja2)
// ============================================================================

const ContactModalHandlers = {
    /**
     * Close and remove the modal
     */
    closeModal() {
        const modal = document.getElementById('contact-modal');
        if (modal) {
            modal.remove();
        }
    },

    /**
     * Update selected count display
     */
    updateSelectedCount() {
        const count = document.querySelectorAll('[data-contact-modal-checkbox]:checked').length;
        const countDisplay = document.getElementById('selected-count');
        if (countDisplay) {
            countDisplay.textContent = `${count} contact${count !== 1 ? 's' : ''} selected`;
        }
    },

    /**
     * Filter contacts by search query
     */
    filterContacts(e, element) {
        const rows = document.querySelectorAll('.contact-row');
        const searchTerm = element.value.toLowerCase();

        rows.forEach(row => {
            const name = (row.dataset.name || '').toLowerCase();
            const email = (row.dataset.email || '').toLowerCase();
            const tags = (row.dataset.tags || '').toLowerCase();

            if (name.includes(searchTerm) || email.includes(searchTerm) || tags.includes(searchTerm)) {
                row.style.display = '';
            } else {
                row.style.display = 'none';
            }
        });
    },

    /**
     * Filter contacts by first letter
     */
    filterByLetter(e, element) {
        const letter = element.dataset.contactModalLetter;
        const rows = document.querySelectorAll('.contact-row');

        rows.forEach(row => {
            if (letter === 'ALL') {
                row.style.display = '';
            } else {
                if (row.dataset.letter === letter) {
                    row.style.display = '';
                } else {
                    row.style.display = 'none';
                }
            }
        });

        // Clear search input
        const searchInput = document.getElementById('contact-search');
        if (searchInput) searchInput.value = '';
    },

    /**
     * Toggle all contact checkboxes
     */
    toggleAllContacts(e, element) {
        const visibleCheckboxes = document.querySelectorAll('.contact-row:not([style*="display: none"]) [data-contact-modal-checkbox]');
        visibleCheckboxes.forEach(checkbox => {
            checkbox.checked = element.checked;
        });
        ContactModalHandlers.updateSelectedCount();
    },

    /**
     * Add selected contacts and close modal
     */
    addSelectedAndClose() {
        const selectedContacts = [];
        const checkboxes = document.querySelectorAll('[data-contact-modal-checkbox]:checked');

        checkboxes.forEach(checkbox => {
            const name = checkbox.dataset.name;
            const email = checkbox.dataset.email;
            selectedContacts.push(formatRecipient(name, email));
        });

        if (selectedContacts.length > 0) {
            // Get the recipient textarea - works for both new-transfer and forward-transfer forms
            const recipientField = document.querySelector('textarea[name="recipients"]');

            if (recipientField) {
                // Add selected contacts to existing value
                const currentValue = recipientField.value.trim();
                const newValue = currentValue
                    ? currentValue + ', ' + selectedContacts.join(', ')
                    : selectedContacts.join(', ');

                recipientField.value = newValue;
            }
        }

        // Close the modal
        ContactModalHandlers.closeModal();
    },

    /**
     * Handle backdrop click to close modal
     */
    handleBackdropClick(e, element) {
        // Only close if clicking directly on the backdrop, not children
        if (e.target === element) {
            ContactModalHandlers.closeModal();
        }
    },

    /**
     * Handle ESC key to close modal
     */
    handleEscKey(e) {
        if (e.key === 'Escape' && document.getElementById('contact-modal')) {
            ContactModalHandlers.closeModal();
        }
    },

    /**
     * Initialize modal when it's added to DOM
     */
    initializeModal() {
        ContactModalHandlers.updateSelectedCount();
    }
};

/**
 * Initialize contact fragment
 * Called from main.js on DOMContentLoaded
 */
export function initContact() {
    if (initialized) return;
    initialized = true;

    // ========================================================================
    // Contact List Events (list.jinja2)
    // ========================================================================
    Events.on('change', 'contact-select-all', ContactHandlers.onSelectAll);
    Events.on('change', 'contact-row-checkbox', ContactHandlers.onRowCheckboxChange);

    // ========================================================================
    // Contact Modal Events (selector_modal.jinja2)
    // ========================================================================
    Events.on('click', 'contact-modal-close', ContactModalHandlers.closeModal);
    Events.on('click', 'contact-modal-backdrop', ContactModalHandlers.handleBackdropClick);
    Events.on('input', 'contact-modal-search', ContactModalHandlers.filterContacts);
    Events.on('click', 'contact-modal-letter', ContactModalHandlers.filterByLetter);
    Events.on('change', 'contact-modal-select-all', ContactModalHandlers.toggleAllContacts);
    Events.on('click', 'contact-modal-add', ContactModalHandlers.addSelectedAndClose);
    Events.on('change', 'contact-modal-checkbox', ContactModalHandlers.updateSelectedCount);

    // ESC key handler for modal
    document.addEventListener('keydown', ContactModalHandlers.handleEscKey);

    // Initialize modal when it's added to DOM via HTMX
    document.addEventListener('htmx:afterSwap', (event) => {
        if (event.target.id === 'contact-modal' || event.target.querySelector('#contact-modal')) {
            ContactModalHandlers.initializeModal();
        }
    });
}
